import * as types from './actionTypes';
import { getRequest, postRequest, patchRequest, putRequest, deleteRequest } from '../../lib/apiUtils';
import { RecipientTypes, CurrencyTypes, Departments } from '../../lib/systemVariables';
import { flattenObj } from '../../lib/utils';
import { getUpdatedRecipientAccountDetails } from './RecipientAccountsActions';
import { TableViews } from '../../lib/constants';
import { clearRecipientSummaryCache } from '../../lib/recipientSummaryCache';

export function getStatusCounts() {
    return async (dispatch, getState) => {
        const requestId = ++getState().applications.statusCounts.currentRequestId;
        dispatch({ type: types.START_GET_APPLICATION_STATUS_COUNTS, requestId });

        const filterSettings = getState().applications.filters;
        let params = {
            paidDaysBack: filterSettings.days.Paid,
            rejectedDaysBack: filterSettings.days.Rejected
        };
        for (var filterProperty in filterSettings) {
            if (filterProperty !== "status" && filterProperty !== "days") {
                let filterVal = filterSettings[filterProperty];
                if (filterProperty === "recipients") {
                    filterVal = filterSettings.recipients.map(rec => rec.applicantId);
                }
                // Only include hasReceipt if it's explicitly true or false, not null
                if (filterProperty === "hasReceipt" && filterVal === null) {
                    continue;
                }
                params[filterProperty] = filterVal;
            }
        }

        const response = await getRequest('/applications/statusCounts', params);

        dispatch({ type: types.GET_APPLICATION_STATUS_COUNTS, ...{ response, requestId } });
    };
}

export function filterApplications(getCounts, update = null, search = "", results = 12, sort = { by: 'applicationDate', direction: 'Descending' }) {
    return dispatch => {
        if (update && update.hasOwnProperty("status")) {
            dispatch({ type: types.CLEAR_APPLICATIONS_TABLE_RECORDS });
        }
        dispatch({ type: update ? types.UPDATE_APPLICATIONS_FILTERS : types.RESET_APPLICATIONS_FILTERS, update });
        dispatch({ type: types.CLEAR_APPLICATIONS_TABLE_SELECTION });

        dispatch(getApplications(search, 1, results, sort));

        if (getCounts)
            dispatch(getStatusCounts());
    }
}

export function searchApplicants(search, page = 1, results = 12) {
    return async (dispatch, getState) => {
        let filterSettings = getState().applications.filters;
        filterSettings.elasticSearchText = search;
        if(search.length > 0) {
            const response = await getRequest('/search/applicants', {text: search, page, results});
            filterSettings = {
                ...filterSettings,
                recipients: response.data && response.data.results ? response.data.results : [],
                status: null,
                days: { ...filterSettings.days, Recent: "" }
            }
        }
        else {
            filterSettings.recipients = [];
        }
        dispatch({ type: types.UPDATE_APPLICATIONS_FILTERS, update: filterSettings });
        dispatch(getApplications(page, results));
    }
}

export function updateTable(settings) {
    return dispatch => {
        dispatch({ type: types.UPDATE_APPLICATIONS_TABLE, settings });
    }
}

export function toggleSelectApplication(application) {
    return dispatch => {
        dispatch({ type: types.TOGGLE_SELECT_APPLICATIONS_TABLE_RECORD, ...{ record: application, recordIdKey: 'applicationId' } });
    }
}

export function clearApplicationsSelection() {
    return dispatch => {
        dispatch({ type: types.CLEAR_APPLICATIONS_TABLE_SELECTION });
    }
}

export function getApplications(search = "", page = 1, results = 12, sort) {
    return async (dispatch, getState) => {
        const filterSettings = getState().applications.filters;
        filterSettings.elasticSearchText = search;
        
        const settings = { page, results, sort: sort || { by: 'applicationDate', direction: 'Descending' } };
        dispatch({ type: types.UPDATE_APPLICATIONS_TABLE, settings });
        const tableSettings = getState().applicationsTable;
        
        const requestId = ++getState().applicationsTable.currentRequestId;
        dispatch({ type: types.START_GET_APPLICATIONS_TABLE_RECORDS, requestId });
        
        let params = {
            page: tableSettings.page,
            sortBy: tableSettings.sort.by,
            sortDirection: tableSettings.sort.direction,
            results: tableSettings.resultsView
        };
        for (var filterProperty in filterSettings) {
            let filterVal = filterSettings[filterProperty];
            if (filterProperty === "view") {
                params.detailed = filterVal === TableViews.Detailed;
            } else if (filterProperty === "hasReceipt") {
                // Only include hasReceipt if it's explicitly true or false, not null
                if (filterVal !== null) {
                    params[filterProperty] = filterVal;
                }
            } else {
                if (filterProperty === "days") {
                    filterVal = filterSettings.days[filterSettings.status ? filterSettings.status : 'Recent'] || '';
                } else if (filterProperty === "recipients") {
                    filterVal = filterSettings.recipients.map(rec => rec.applicantId);
                }
                params[filterProperty] = filterVal;
            }
        }

        const response = await getRequest('/applications', params);

        dispatch({ type: types.GET_APPLICATIONS_TABLE_RECORDS, ...{ response, requestId } });
    };
}

export function getApplicationsToRecipientIdsMapping(campaignId, campaignListId) {
    return async (dispatch, getState) => {
        dispatch({ type: types.START_GET_APPLICATIONS_TO_RECIPIENT_IDS_MAPPING });

        let tableSettings = {};

        if (campaignId && campaignListId) {
            tableSettings = {
                sort: { by: 'lastOrOrgName', direction: 'Ascending'}
            };
        } else {
            tableSettings = campaignId
            ? getState().campaignDetailsIndividualsTable
            : campaignListId
                ? getState().campaignListParticipantsTable
                : getState().applicationsTable;

        }

        const filterSettings = campaignId
            ? !campaignListId ? { campaignIndividuals: true, campaigns: [campaignId] } 
                              : {campaigns: [campaignId], campaignLists: [campaignListId], 
                                applicationStatuses: [getState().campaignListApplications.applicationStatus]}
            : campaignListId
                ? { campaignListIndividuals: true, campaignLists: [campaignListId] }
                : getState().applications.filters;
        
        let params = {
            page: tableSettings.page,
            sortBy: tableSettings.sort.by,
            sortDirection: tableSettings.sort.direction
        };

        if (campaignListId) {
            params.verbose = false;
        }
        
        for (var filterProperty in filterSettings) {
            let filterVal = filterSettings[filterProperty];
            
            if (filterProperty === "days") {
                filterVal = filterSettings.days[filterSettings.status ? filterSettings.status : 'Recent'] || '';
            } else if (filterProperty === "recipients") {
                filterVal = filterSettings.recipients.map(rec => rec.applicantId);
            }
            
            // Only include hasReceipt if it's explicitly true or false, not null
            if (filterProperty === "hasReceipt" && filterVal === null) {
                continue;
            }
            params[filterProperty] = filterVal;
        }

        if (campaignListId && !campaignId){
            const response = await getRequest(`/campaignLists/${campaignListId}/participants`, params, []);
            let mapping = {};
            if (response.data)
                response.data.forEach(ids => mapping[ids.applicantId] = null);
                
            response.data = {
                mapping,
                orderedApplications: response.data.map(ids => ids.applicantId)
            };

            dispatch({ type: types.GET_APPLICATIONS_TO_RECIPIENT_IDS_MAPPING, response });
        } else {
            const response = await getRequest('/applications/recipients', params, []);
            let mapping = {};
            if (response.data)
                response.data.forEach(ids => mapping[ids.applicationId] = ids.recipientId);
                
            response.data = {
                mapping,
                orderedApplications: response.data.map(ids => ids.applicationId)
            };

            dispatch({ type: types.GET_APPLICATIONS_TO_RECIPIENT_IDS_MAPPING, response });
        }
    };
}

export function deleteApplication(applicationId) {
    return async dispatch => {
        dispatch({ type: types.START_REMOVE_APPLICATIONS_TABLE_RECORD });

        const response = await deleteRequest(`/applications/${applicationId}`);
        if (response.success) clearRecipientSummaryCache();
        response.recordIdKey = 'applicationId';
        response.recordId = applicationId;

        dispatch({ type: types.REMOVE_APPLICATIONS_TABLE_RECORD, response });
        return response;
    };
}

export function getApplicationDetails(applicationId, updateHistory, recipientId) {

    return async dispatch => {
        dispatch({ type: types.START_GET_APPLICATION_DETAILS });

        const response = recipientId
            ? await getRequest(`/applications/${applicationId}`, { recipientId })
            : await getRequest(`/applications/${applicationId}`);

        dispatch({ type: types.GET_APPLICATION_DETAILS, response });

        if (response.success && updateHistory) {
            //this is being used for when a user edits an application (not wire/void/print) and have saved a manual check so the total needs to be updated.
            //or when a user edits an application and chooses a new source or recipient reference/trusted payee as payee, so isUsedAsPayee needs to be updated for reicpient references.
            if (recipientId) dispatch(getUpdatedRecipientAccountDetails(recipientId));

            dispatch({
                type: types.UPDATE_APPLICATION_DETAILS,
                ...{
                    applicationId,
                    update: {
                        status: response.data.status,
                        amount: response.data.amount,
                        lastModified: response.data.history ? response.data.history[0].date : Date.now(),
                        reason: response.data.reason ? response.data.reason.reason : '',
                        currency: response.data.currency
                    }
                }
            });
        }
    };
}

export function updateApplication(application, update) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATIONS_TABLE_RECORD, recordId: application.applicationId });

        const response = await patchRequest(`/applications/${application.applicationId}`, update);
        if (response.success) clearRecipientSummaryCache(application.recipient && application.recipient.recipientId);
        response.recordIdKey = 'applicationId';
        response.recordId = application.applicationId;
        response.update = { ...update, lastModified: Date.now() }; //Note - This code can be optimized.  Data may be out of sync with server being that we are artificially updating lastModified property to avoid additional server request.  

        dispatch({ type: types.UPDATE_APPLICATIONS_TABLE_RECORD, response });
        return response;
    };
}

export function updateApplicationsStatus(action, applications) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATIONS_STATUS });

        const response = await postRequest(`/applications/${action}`, applications);
        if (response.success) clearRecipientSummaryCache();

        dispatch({ type: types.UPDATE_APPLICATIONS_STATUS_COMPLETED, response });
    };
}

export function revertApplicationStatus(applicationId) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATIONS_STATUS });

        const response = await postRequest('/applications/revertStatus', applicationId);
        if (response.success) clearRecipientSummaryCache();

        dispatch({ type: types.UPDATE_APPLICATIONS_STATUS_COMPLETED, response });
    };
}

export function wireApplications(applicationIds) {
    return async () => {
        const response = await postRequest('/applications/wire', applicationIds, []);
        if (response.success) clearRecipientSummaryCache();
        return response;
    };
}

export function sendACHApplications(applicationIds) {
      return async dispatch => {
        dispatch({ type: types.START_SEND_ACH_APPLICATIONS });

        const response = await postRequest('/ach/send', applicationIds);
        if (response.success) clearRecipientSummaryCache();

        dispatch({ type: types.SEND_ACH_APPLICATIONS_COMPLETED, response });
            return response;
    };
}

export function voidApplicationPayment(applicationIds) {
    return async dispatch => {
        dispatch({ type: types.START_VOID_APPLICATION_PAYMENT });

        const response = await postRequest('/applications/void', applicationIds);
        if (response.success) clearRecipientSummaryCache();

        dispatch({ type: types.VOID_APPLICATION_PAYMENT_COMPLETED, response });
    };
}

async function getNote(applicationId, noteId) {
    return await getRequest(`/applications/${applicationId}/notes/${noteId}`);
}

export function addApplicationNote(applicationId, noteText) {
    return async dispatch => {
        dispatch({ type: types.START_ADD_APPLICATION_NOTE });

        const response = await postRequest(`/applications/${applicationId}/notes`, { noteText });
        if (response.success) clearRecipientSummaryCache();

        if (response.success) {
            const note = await getNote(applicationId, response.data);
            response.data = note.data;
        }

        dispatch({ type: types.ADD_APPLICATION_NOTE, response });
    };
}

export function updateApplicationNote(applicationId, noteId, noteText) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATION_NOTE });

        const response = await putRequest(`/applications/${applicationId}/notes/${noteId}`, { noteText });
        if (response.success) clearRecipientSummaryCache();

        const note = await getNote(applicationId, noteId);
        response.data = note.data;

        dispatch({ type: types.UPDATE_APPLICATION_NOTE, response });
    };
}

export function deleteApplicationNote(applicationId, noteId) {
    return async dispatch => {
        dispatch({ type: types.START_DELETE_APPLICATION_NOTE });

        const response = await deleteRequest(`/applications/${applicationId}/notes/${noteId}`);
        if (response.success) clearRecipientSummaryCache();

        dispatch({ type: types.DELETE_APPLICATION_NOTE, ...{ response, noteId } });
    };
}

export function updateApplicationAttachments(applicationId, attachments) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATION_ATTACHMENTS });

        const formData = new FormData();
        attachments.newAttachments.map(attachment => formData.append('newAttachments', attachment));
        attachments.deletedAttachments.map(attachment => formData.append('deletedAttachments', attachment));

        const response = await putRequest(`/applications/${applicationId}/attachments`, formData, { 'Content-Type': 'multipart/form-data' }, {});

        const updatedAttachments = await getRequest(`/applications/${applicationId}/attachments`);
        response.updatedAttachments = updatedAttachments;

        dispatch({ type: types.UPDATE_APPLICATION_ATTACHMENTS, response });
    }
}

export function deleteApplicationReceipt(applicationId, receiptId) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATION_RECEIPTS });

        const response = await deleteRequest(`/applications/${applicationId}/receipts/${receiptId}`);

        if (response.success) {
            const updatedReceipts = await getRequest(`/applications/${applicationId}/receipts`);
            response.updatedReceipts = updatedReceipts;
        }

        dispatch({ type: types.UPDATE_APPLICATION_RECEIPTS, response });
    }
}

export function updateApplicationReceipts(applicationId, receipts) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATION_RECEIPTS });

        const formData = new FormData();
        // Append receipts
        if (receipts.newReceipts && Array.isArray(receipts.newReceipts)) {
            receipts.newReceipts.forEach(receipt => {
                formData.append('receipts', receipt);
            });
        }

        const response = await putRequest(`/applications/${applicationId}/receipts`, formData, {}, {});

        if (response.success) {
            const updatedReceipts = await getRequest(`/applications/${applicationId}/receipts`);
            response.updatedReceipts = updatedReceipts;
        }

        dispatch({ type: types.UPDATE_APPLICATION_RECEIPTS, response });
    }
}

export function updateApplicationDetails(applicationId, application, bypass = false) {
    return async dispatch => {
        dispatch({ type: types.START_UPDATE_APPLICATION });
        const formData = new FormData();
        const flatObj = flattenObj(application);
        for (const prop in flatObj) formData.append(prop, flatObj[prop] !== null ? flatObj[prop] : '');

        application.newAttachments.map(attachment => formData.append('newAttachments', attachment));
        application.deletedAttachments.map(attachment => formData.append('deletedAttachments', attachment));

        const url = bypass === true ? `/applications/${applicationId}?bypassError=${bypass}` : `/applications/${applicationId}`;
        const response = await putRequest(url, formData, { 'Content-Type': 'multipart/form-data' }, {});
        if (response.success) clearRecipientSummaryCache();
        dispatch({ type: types.UPDATE_APPLICATION, response });
    }
}

export function updateSelectedCounts(selectedApplications, userProfile, funds) {
    return dispatch => {
        const counts = calculateActionCounts(selectedApplications, userProfile, funds);
        dispatch({ type: types.UPDATE_SELECTED_COUNTS, counts });
    };
}



function calculateActionCounts(selectedApplications, userProfile, funds) {

    const isUSUser = userProfile.departments && userProfile.departments.indexOf(Departments.US) >= 0;
    const isIsraelUser = userProfile.departments && userProfile.departments.indexOf(Departments.IL) >= 0;
    
    let checkCount = 0;
    let achCount = 0;
    let wireCount = 0;
    
    selectedApplications.forEach(app => {
        const hasEligibleRecipient =
            app.recipient &&
            app.recipient.hasAchAccount === true &&
            app.recipient.applicantType === RecipientTypes.Organization;

        if (app.currency === CurrencyTypes.Dollar && isUSUser) {

            if (hasEligibleRecipient && app.payTo === "Recipient" && app.fundAllowsACH === true) {
                achCount++;
                checkCount++;
            } else {
                checkCount++;
            }
        } else if (app.currency === CurrencyTypes.Shekel && isIsraelUser) {
            wireCount++;
        }
    });
    
    return { check: checkCount, ach: achCount, wire: wireCount };
}


// WEBPACK FOOTER //
// ./src/redux/actions/ApplicationsActions.js